import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Card,
  List,
  Input,
  Button,
  Upload,
  Tag,
  Avatar,
  Space,
  message,
  Tooltip,
  Typography,
  Divider,
} from 'antd';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import '../ImageGallery.css';
import {
  SendOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { customizationAPI } from '../api';
import { compressImage, isImageFile, formatFileSize } from '../utils/imageCompression';

const { TextArea } = Input;
const { Text, Title } = Typography;

const CustomizationDialog = ({ customization, visible, onClose }) => {
  const [customizationData, setCustomizationData] = useState(customization);
  const [loading, setLoading] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [fileList, setFileList] = useState([]);
  const [imageUrls, setImageUrls] = useState({}); // 存储图片blob URLs
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const [modal, modalContextHolder] = Modal.useModal();

  useEffect(() => {
    if (visible && customization) {
      fetchCustomizationDetails();
    }
  }, [visible, customization]);

  useEffect(() => {
    scrollToBottom();
  }, [customizationData?.comments]);

  // 清理blob URLs
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, [imageUrls]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchCustomizationDetails = async () => {
    try {
      const response = await customizationAPI.getById(customization.id);
      // 按创建时间降序排序评论，最晚创建的排在最上面
      const sortedData = {
        ...response.data,
        comments: response.data.comments?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) || []
      };
      setCustomizationData(sortedData);

      // 获取所有图片的blob URLs
      await loadImageUrls(sortedData);
    } catch (error) {
      message.error('Failed to fetch customization details');
      
    }
  };

  const loadImageUrls = async (customizationData) => {
    const newImageUrls = {};

    for (const comment of customizationData.comments || []) {
      for (const attachment of comment.attachments || []) {
        if (attachment.content_type.startsWith('image/')) {
          const imageKey = `${comment.id}-${attachment.filename}`;
          try {
            const response = await customizationAPI.downloadAttachment(customization.id, comment.id, attachment.filename);
            const blob = new Blob([response.data], { type: attachment.content_type });
            const url = window.URL.createObjectURL(blob);
            newImageUrls[imageKey] = url;
          } catch (error) {
            
          }
        }
      }
    }

    setImageUrls(newImageUrls);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() && fileList.length === 0) {
      message.warning('Please enter a message or attach files');
      return;
    }

    // 如果定制需求已经是approved状态，需要用户确认
    if (customizationData?.status === 'approved') {
      modal.confirm({
        title: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Warning</span>
          </div>
        ),
        content: (
          <div style={{ padding: '16px 0' }}>
            <p style={{ margin: '12px 0', fontSize: '14px' }}>
              Sending a new message will change this customization's status from 
              <strong style={{ color: '#52c41a' }}> approved</strong> to 
              <strong style={{ color: '#fa8c16' }}> unapproved</strong>.
            </p>
            <p style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>
              We need to review your new request and approve it again.
            </p>
            <p style={{ margin: '12px 0', fontSize: '14px', fontWeight: 'bold' }}>
              Do you want to continue sending this message?
            </p>
          </div>
        ),
        okText: 'Yes, Send Message',
        cancelText: 'Cancel',
        okType: 'primary',
        width: 480,
        onOk: () => {
          sendMessageConfirmed();
        },
        onCancel: () => {
          // 用户取消，不做任何操作
        }
      });
      return;
    }

    // 如果不是approved状态，直接发送
    sendMessageConfirmed();
  };

  const sendMessageConfirmed = async () => {
    setLoading(true);
    try {
      // 处理附件（压缩图片）
      const processedFiles = [];
      for (const file of fileList) {
        if (isImageFile(file.originFileObj)) {
          // 图片文件：检查大小并压缩
          if (file.originFileObj.size > 1024 * 1024) { // 超过1MB
            message.loading(`Compressing image ${file.name}...`, 0);
            try {
              const compressedFile = await compressImage(file.originFileObj, 700); // 压缩到700KB
              processedFiles.push(compressedFile);
              message.destroy();
              message.success(`Image ${file.name} compressed (${formatFileSize(file.originFileObj.size)} → ${formatFileSize(compressedFile.size)})`);
            } catch (error) {
              message.destroy();
              message.warning(`Image ${file.name} compression failed, using original file`);
              processedFiles.push(file.originFileObj);
            }
          } else {
            processedFiles.push(file.originFileObj);
          }
        } else {
          // 非图片文件：直接添加
          processedFiles.push(file.originFileObj);
        }
      }

      const formData = new FormData();
      formData.append('content', messageText);

      // 添加处理后的附件
      processedFiles.forEach((file) => {
        formData.append('files', file);
      });

      await customizationAPI.addComment(customization.id, formData);
      message.success('Message sent successfully');
      setMessageText('');
      setFileList([]);
      fetchCustomizationDetails(); // Refresh the conversation
    } catch (error) {
      message.error('Failed to send message');
      
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment, commentId) => {
    try {
      const response = await customizationAPI.downloadAttachment(customization.id, commentId, attachment.filename);

      // Create blob URL and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error('Failed to download attachment');
      
    }
  };

  const handleImagePreview = (commentId, attachmentFilename) => {
    // 收集所有图片附件
    const allImages = [];
    let targetIndex = 0;
    let currentIndex = 0;

    customizationData?.comments?.forEach(comment => {
      comment.attachments?.forEach(attachment => {
        if (attachment.content_type.startsWith('image/')) {
          const imageKey = `${comment.id}-${attachment.filename}`;
          const imageUrl = imageUrls[imageKey];

          if (imageUrl) {
            allImages.push({
              original: imageUrl,
              thumbnail: imageUrl,
              description: `${attachment.original_filename} - ${comment.user?.username}`,
              originalAlt: attachment.original_filename,
              thumbnailAlt: attachment.original_filename,
            });

            // 如果是当前点击的图片，记录索引
            if (comment.id === commentId && attachment.filename === attachmentFilename) {
              targetIndex = currentIndex;
            }
            currentIndex++;
          }
        }
      });
    });

    setGalleryImages(allImages);
    setCurrentImageIndex(targetIndex);
    setGalleryVisible(true);
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) return <FileImageOutlined />;
    if (contentType === 'application/pdf') return <FilePdfOutlined />;
    if (contentType.includes('word')) return <FileWordOutlined />;
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return <FileExcelOutlined />;
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return <FilePptOutlined />;
    return <FileOutlined />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type)) {
        message.error('File type not supported. Please upload images, PDF, DOC, DOCX, PPT, PPTX, XLS, or XLSX files.');
        return false;
      }

      // 图片文件限制1MB（会自动压缩），其他文件限制3MB
      const isImage = file.type.startsWith('image/');
      const maxSize = isImage ? 1024 * 1024 : 3 * 1024 * 1024; // 1MB for images, 3MB for others
      const sizeLabel = isImage ? '1MB' : '3MB';

      if (file.size > maxSize) {
        if (isImage) {
          message.warning(`Image ${file.name} exceeds 1MB, will be automatically compressed before upload`);
          // 图片文件允许添加，因为会自动压缩
        } else {
          message.error(`File size cannot exceed ${sizeLabel}`);
          return Upload.LIST_IGNORE; // 阻止文件添加到列表
        }
      }

      return false; // Don't auto upload
    },
    fileList,
    onChange: ({ fileList: newFileList }) => {
      setFileList(newFileList);
    },
    multiple: true,
    accept: ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx",
    listType: "picture-card",
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
      showDownloadIcon: false,
    },
  };

  return (
    <>
      {modalContextHolder}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Customization: {customizationData?.customization_name}</span>
            <Tag
              color={customizationData?.status === 'approved' ? 'green' : 'blue'}
              icon={customizationData?.status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            >
              {customizationData?.status === 'approved' ? 'Approved' : 'Unapproved'}
            </Tag>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width="95vw"
        footer={null}
        style={{ top: 20, maxWidth: '1400px' }}
        centered
      >
        <div style={{ height: '75vh', display: 'flex', flexDirection: 'column' }}>
          {/* Messages Area */}
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: 16, padding: '0 8px' }}>
            <List
              dataSource={customizationData?.comments || []}
              renderItem={(comment) => (
                <List.Item style={{ border: 'none', padding: '8px 0' }}>
                  <Card
                    size="small"
                    style={{
                      width: '100%',
                      backgroundColor: comment.user?.role === 'admin' ? '#f0f8ff' : '#f9f9f9',
                      borderLeft: `4px solid ${comment.user?.role === 'admin' ? '#1890ff' : '#52c41a'}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <Avatar
                        icon={<UserOutlined />}
                        style={{
                          backgroundColor: comment.user?.role === 'admin' ? '#1890ff' : '#52c41a'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <Text strong>{comment.user?.username}</Text>
                          <Tag
                            size="small"
                            color={comment.user?.role === 'admin' ? 'blue' : 'green'}
                          >
                            {comment.user?.role?.toUpperCase()}
                          </Tag>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </Text>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Text>{comment.content}</Text>
                        </div>
                        {comment.attachments && comment.attachments.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <Divider style={{ margin: '8px 0' }} />
                            <Text type="secondary" style={{ fontSize: '12px' }}>Attachments:</Text>

                            {/* 图片附件 - 横向排列 */}
                            {comment.attachments.filter(att => att.content_type.startsWith('image/')).length > 0 && (
                              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {comment.attachments
                                .filter(attachment => attachment.content_type.startsWith('image/'))
                                .map((attachment, index) => (
                                  <div
                                    key={`${comment.id}-${attachment.filename}-${index}`}
                                    style={{
                                      border: '1px solid #d9d9d9',
                                      borderRadius: 6,
                                      padding: 4,
                                      cursor: 'pointer',
                                      maxWidth: 120,
                                    }}
                                    onClick={() => handleImagePreview(comment.id, attachment.filename)}
                                  >
                                    {imageUrls[`${comment.id}-${attachment.filename}`] ? (
                                      <img
                                        src={imageUrls[`${comment.id}-${attachment.filename}`]}
                                        alt={attachment.original_filename}
                                        style={{
                                          width: '100%',
                                          height: 80,
                                          objectFit: 'cover',
                                          borderRadius: 4,
                                          display: 'block'
                                        }}
                                      />
                                    ) : (
                                      <div style={{ height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', borderRadius: 4 }}>
                                        <FileImageOutlined style={{ fontSize: 24, color: '#999' }} />
                                      </div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                                      <div style={{ fontSize: '11px', textAlign: 'center', wordBreak: 'break-all', flex: 1 }}>
                                        {attachment.original_filename.length > 15
                                          ? attachment.original_filename.substring(0, 12) + '...'
                                          : attachment.original_filename}
                                      </div>
                                      <Tooltip title="Download">
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<DownloadOutlined />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadAttachment(attachment, comment.id);
                                          }}
                                          style={{ padding: '2px', minWidth: 'auto' }}
                                        />
                                      </Tooltip>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}

                          {/* 文档附件 - 竖向排列，每行一个 */}
                          {comment.attachments.filter(att => !att.content_type.startsWith('image/')).length > 0 && (
                            <div style={{ marginTop: 8 }}>
                              {comment.attachments
                                .filter(attachment => !attachment.content_type.startsWith('image/'))
                                .map((attachment, index) => (
                                  <div key={`${comment.id}-${attachment.filename}-doc-${index}`} style={{ marginBottom: 4 }}>
                                    <Tag
                                      icon={getFileIcon(attachment.content_type)}
                                      style={{
                                        cursor: 'pointer',
                                        maxWidth: '300px',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                      }}
                                      onClick={() => handleDownloadAttachment(attachment, comment.id)}
                                    >
                                      <Space size={6}>
                                        <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {attachment.original_filename}
                                        </span>
                                        <Text type="secondary" style={{ fontSize: '10px' }}>
                                          ({formatFileSize(attachment.file_size)})
                                        </Text>
                                        <DownloadOutlined />
                                      </Space>
                                    </Tag>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </List.Item>
            )}
          />
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input Area */}
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Upload {...uploadProps}>
              <Button icon={<PaperClipOutlined />} size="small">
                Attach Files
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {fileList.length} file(s) selected
                </Text>
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Supported file types: images and documents (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX). File size cannot exceed 3MB for documents.
              </Text>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <TextArea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message here..."
              rows={3}
              style={{ flex: 1 }}
              onPressEnter={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  handleSendMessage();
                }
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={loading}
              style={{ alignSelf: 'flex-end' }}
            >
              Send
            </Button>
          </div>
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Press Ctrl+Enter to send message
            </Text>
          </div>
        </div>
      </div>

      </Modal>

      {/* Image Gallery Modal */}
      <Modal
        open={galleryVisible}
        onCancel={() => setGalleryVisible(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        centered
        className="image-gallery-modal"
      >
        {galleryImages.length > 0 && (
          <ImageGallery
            items={galleryImages}
            startIndex={currentImageIndex}
            showThumbnails={galleryImages.length > 1}
            showPlayButton={false}
            showFullscreenButton={true}
            showNav={galleryImages.length > 1}
            slideDuration={300}
            slideInterval={0}
            onSlide={(index) => setCurrentImageIndex(index)}
            additionalClass="custom-image-gallery"
          />
        )}
      </Modal>
    </>
  );
};

export default CustomizationDialog;
